import { FC, SyntheticEvent, useState } from 'react'
import { Box, IconButton, Link as MuiLink, Tab, Typography } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'

/* Utils */
import { CategoryFramework, CPILibraryItem, Tenant } from 'models'
import { categoryToUrl } from '../../library/utils'
import { a11yProps } from './LibraryDetailsTabPanel'
import { CustomColors } from '../CPIDetails'
import { DevicesTabs } from '../useLibraryDetailsData'
import { useLibraryDetails } from '../../../../contexts/LibraryDetailsContext'

/* Components */
import { LibraryDetailsHoverTooltip } from '../../../components/tooltip/LibraryDetailsTooltip'
import { ActiveCPIIcon, FavoriteCPIIcon, InactiveCpiLibraryIcon, NotFavoriteCPIIcon } from '../../../components/svg'
import { IntegrationsTabs, IntegrationsTabsWrapper } from '../../settings/integrations/integrations.styles'
import { HeaderBreadcrumbs, LibraryDetailsHeaderContainer } from './libraryDetailsHeader.styles'
import NistFrameworkCategory from '../../../components/nist-framework-category'
import CpiNavigation from '../sections/devices/components/CpiNavigation'
import LibraryDetailsHeaderActions from '../sections/library-details-header/header-actions/LibraryDetailsHeaderActions'
import TagLabel from './TagLabel'

interface Props {
  accountSettings: Tenant | null
  favorite: boolean
  updateFavorite: () => void
  handleReportExport: () => void
  isExportingReport: boolean
  showReport?: boolean
  disableReport?: boolean
  customColors: CustomColors | null
  handlePredictionModalClose: (hideNotification?: boolean) => void
  initialQueryLoaded: boolean
  columnsFetched: boolean
  setColumnsFetched: (value: boolean) => void
  activeCpis: CPILibraryItem[]
}

const LibraryDetailsHeader: FC<Props> = ({
  favorite,
  handlePredictionModalClose,
  updateFavorite,
  handleReportExport,
  isExportingReport,
  accountSettings,
  showReport = true,
  disableReport = false,
  customColors,
  columnsFetched,
  setColumnsFetched,
  activeCpis
}) => {
  const {
    cpi,
    active,
    manifestData,
    setDrawerOpen,
    tabValue,
    setTabValue,
    snowflakeManifest,
    showBenchmarkingTab,
    currentSeverityStatus,
    isSnowflake
  } = useLibraryDetails()
  /* Call this hook before the early return below. */
  const queryClient = useQueryClient()

  if (!manifestData) return null

  const navigate = useNavigate()
  const [favoriteLoading, setFavoriteLoading] = useState(false)

  const handleFavorite = async () => {
    setFavoriteLoading(true)

    try {
      updateFavorite()
    } catch (e) {
      console.error(e)
    } finally {
      setFavoriteLoading(false)
    }
  }

  const handleOpenDrawer = () => {
    setDrawerOpen(true)
  }

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    if (active) {
      if (snowflakeManifest) {
        const val =
          newValue === 1
            ? DevicesTabs.OperationalView
            : newValue === 2
            ? DevicesTabs.About
            : newValue === 3
            ? DevicesTabs.Benchmarking
            : DevicesTabs.Trends
        // Reset columnsFetched when switching to Off-SLA Entities tab to show loading skeleton
        if (val === DevicesTabs.OperationalView) {
          setColumnsFetched(false)
        }
        setTabValue(val)
        navigate(
          `/library/cpis/${cpi?.display_name.toLowerCase() || ''}/${
            val === DevicesTabs.OperationalView ? 'off-sla-entities' : val
          }`,
          { replace: true }
        )
      } else {
        const val =
          newValue === 1 && active ? DevicesTabs.About : newValue === 2 ? DevicesTabs.Benchmarking : DevicesTabs.Trends
        setTabValue(val)
        navigate(`/library/cpis/${cpi?.display_name.toLowerCase() || ''}/${val}`, { replace: true })
      }
    } else {
      const val = newValue === 0 ? DevicesTabs.About : newValue === 1 ? DevicesTabs.Benchmarking : DevicesTabs.Trends
      setTabValue(val)
      navigate(`/library/cpis/${cpi?.display_name.toLowerCase() || ''}/${val}`, { replace: true })
    }
  }

  const handleCategoryClick = () => {
    const cat = manifestData.category?.title ? manifestData.category.title : manifestData.categories[0]
    const url = categoryToUrl(cat)
    navigate(`/library/categories/${url === 'vulnerabilty-management' ? 'vulnerability-management' : url}`)
  }

  return (
    <LibraryDetailsHeaderContainer
      severity={currentSeverityStatus}
      category={manifestData.category}
      customColors={customColors || undefined}
      className="library-details-header-container"
    >
      <Box className="breadcrumbs-wrapper">
        <HeaderBreadcrumbs aria-label="breadcrumb">
          <MuiLink component={Link} color="inherit" to="/">
            Home
          </MuiLink>
          <MuiLink
            component={Link}
            color="inherit"
            to="/library"
            onClick={() => queryClient.invalidateQueries(['cpis'])}
          >
            Indicator Library
          </MuiLink>
          <CpiNavigation
            label=""
            options={activeCpis}
            version="cpi"
            cpiName={cpi?.display_name || ''}
            handlePredictionModalClose={handlePredictionModalClose}
          />
        </HeaderBreadcrumbs>
      </Box>

      <LibraryDetailsHeaderActions
        showReport={showReport}
        disabledReport={disableReport}
        isExportingReport={isExportingReport}
        handleReportExport={handleReportExport}
        handleOpenDrawer={handleOpenDrawer}
      />

      <Box className="title-wrapper">
        <LibraryDetailsHoverTooltip title={!favorite ? 'Favorite' : 'Remove Favorite'} placement="bottom-start">
          <IconButton
            onClick={handleFavorite}
            disabled={favoriteLoading}
            className={favorite ? 'favorited' : undefined}
          >
            {favorite ? <FavoriteCPIIcon /> : <NotFavoriteCPIIcon />}
          </IconButton>
        </LibraryDetailsHoverTooltip>
        <Typography className="header-title">{manifestData.title}</Typography>
      </Box>

      <Box className="chips-wrapper">
        {accountSettings?.cyFramework === CategoryFramework.NIST ? (
          <NistFrameworkCategory
            toLink={handleCategoryClick}
            cpiName={manifestData.display_name}
            categoryId={manifestData.category?.id}
            categoryName={manifestData.category?.title}
          />
        ) : accountSettings?.cyFramework === CategoryFramework.Onyxia ? (
          <Box className="cpi-category" onClick={handleCategoryClick}>
            {manifestData.category.title}
          </Box>
        ) : (
          <>
            {manifestData.category && (
              <Box className="cpi-category custom" onClick={handleCategoryClick}>
                {manifestData.category.title}
              </Box>
            )}
          </>
        )}
        {active ? (
          <Box className="cpi-active-button cpi-active">
            <ActiveCPIIcon />
            Active
          </Box>
        ) : (
          <Box className="cpi-active-button cpi-inactive">
            <InactiveCpiLibraryIcon />
            Inactive
          </Box>
        )}

        <Box className="cpi-framework-panel">
          {manifestData.cybersecurity_frameworks &&
            manifestData.cybersecurity_frameworks.map((framework) => {
              if (framework.display_name === 'Gartner') return null
              return (
                <Box className="cpi-framework" key={framework.logo_path}>
                  <img src={`/${framework.logo_path}`} alt={framework.name} />
                  <Typography className="framework-title">{framework.display_name}</Typography>
                </Box>
              )
            })}
        </Box>
      </Box>

      <IntegrationsTabsWrapper custom={true}>
        {active ? (
          <>
            {snowflakeManifest ? (
              <IntegrationsTabs
                value={
                  tabValue === DevicesTabs.OperationalView
                    ? 1
                    : tabValue === DevicesTabs.Trends
                    ? 0
                    : tabValue === DevicesTabs.About
                    ? 2
                    : tabValue === DevicesTabs.Benchmarking
                    ? 3
                    : 0
                }
                onChange={handleTabChange}
                aria-label="library details tabs"
                TabIndicatorProps={{
                  style: { display: 'none' }
                }}
                isdetails="true"
              >
                <Tab label="Data Trends" {...a11yProps(0)} />
                <Tab
                  label={
                    <TagLabel
                      header="Off-SLA Entities"
                      isSnowflake={isSnowflake}
                      isActive={tabValue === DevicesTabs.OperationalView}
                    />
                  }
                  {...a11yProps(1)}
                  disabled={!columnsFetched}
                />
                <Tab label="About" {...a11yProps(2)} />
                {showBenchmarkingTab && <Tab label="Benchmarking" {...a11yProps(3)} />}
              </IntegrationsTabs>
            ) : (
              <IntegrationsTabs
                value={
                  tabValue === DevicesTabs.About
                    ? 1
                    : tabValue === DevicesTabs.Trends
                    ? 0
                    : tabValue === DevicesTabs.Benchmarking
                    ? 2
                    : 0
                }
                onChange={handleTabChange}
                aria-label="library details tabs"
                TabIndicatorProps={{
                  style: { display: 'none' }
                }}
                isdetails="true"
              >
                <Tab label="Data Trends" {...a11yProps(0)} />
                <Tab label="About" {...a11yProps(1)} />
                {showBenchmarkingTab && <Tab label="Benchmarking" {...a11yProps(2)} />}
              </IntegrationsTabs>
            )}
          </>
        ) : (
          <IntegrationsTabs
            value={tabValue === DevicesTabs.About ? 0 : tabValue === DevicesTabs.Benchmarking ? 1 : 0}
            onChange={handleTabChange}
            aria-label="library details tabs"
            TabIndicatorProps={{
              style: { display: 'none' }
            }}
            isdetails="true"
          >
            <Tab label="About" {...a11yProps(0)} />
            {showBenchmarkingTab && <Tab label="Benchmarking" {...a11yProps(1)} />}
          </IntegrationsTabs>
        )}
      </IntegrationsTabsWrapper>
    </LibraryDetailsHeaderContainer>
  )
}

export default LibraryDetailsHeader
